import { Trans } from "@lingui/react/macro"
import { IconQuote } from "@tabler/icons-react"
import { speakerVMFromName } from "../../../../lib/speaker-vm"
import { EditableAnalysisField } from "../../../EditableAnalysisField"
import { SpeakerChip } from "../../../SpeakerChip"
import { findQuoteTimestamp } from "../../helpers"
import { SeekControl } from "../../parts/SeekControl"
import classes from "../../styles.module.css"
import type { IProps } from "./interfaces"

/**
 * QuotesCard renders one card per speaker (the analyser already groups quotes
 * by speaker), listing that speaker's quotes beneath a single avatar + name.
 * Each quote keeps its own "jump to this moment" seek (when a streaming
 * segment matches) and its own click-to-edit path
 * (`speaker_quotes[group].quotes[item]`).
 */
export function QuotesCard({
  quotes,
  speakerColorMap,
  segments,
  resolveSpeaker,
  editContext,
}: IProps): React.JSX.Element | null {
  const groups = (quotes ?? []).filter((g) => g.quotes.length > 0)
  if (groups.length === 0) return null

  const total = groups.reduce((n, g) => n + g.quotes.length, 0)

  return (
    <section className={classes.card} data-testid="quotes-card">
      <div className={classes.cardHead}>
        <h2 className={classes.cardTitle}>
          <IconQuote size={16} />
          <Trans>Quotes</Trans>
        </h2>
        <span className={classes.cardCount}>{total}</span>
      </div>
      <div className={classes.quotesGrid}>
        {groups.map((group, g) => {
          // The chip resolves colour, initials, `Remote::` stripping, the unified
          // "Speaker N" number and the presence-driven "(remote)" suffix from one
          // VM. Prefer the meeting's real view-model (carries display number +
          // presence) over a name-only synthesis, which has neither.
          const vm = resolveSpeaker?.(group.speaker) ?? speakerVMFromName(group.speaker, speakerColorMap)
          return (
            <div
              key={`${g}-${group.speaker}`}
              className={classes.attendeeCard}
              data-testid="quote-card"
              // --owner-color tints the quote background. The name + avatar
              // carry their own colour via the chip.
              style={{ ["--owner-color" as string]: vm.resolvedColor }}
            >
              <div className={classes.attendeeTop}>
                <SpeakerChip variant="avatar" size="lg" speaker={vm} />
                <SpeakerChip variant="inline" speaker={vm} />
              </div>
              {group.quotes.map((text, q) => {
                const ts = findQuoteTimestamp(text, group.speaker, segments)
                const path = `speaker_quotes.${g}.quotes.${q}`
                return (
                  <div key={`${g}-${q}`} className={classes.quoteItem}>
                    <p className={classes.attendeeQuote}>
                      {editContext ? (
                        <EditableAnalysisField
                          meetingId={editContext.meetingId}
                          fieldPath={path}
                          value={text}
                          canEdit={editContext.canEdit}
                          isEdited={!!editContext.fieldEdits[path]}
                          onSaved={editContext.onEdited}
                        />
                      ) : (
                        text
                      )}
                    </p>
                    <SeekControl seekTime={ts} />
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>
    </section>
  )
}
